import { useRef } from "react";
import { Drum } from "lucide-react";

const useAudioCtx = () => {
  const ref = useRef(null);
  return () => {
    if (!ref.current) {
      ref.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ref.current.state === "suspended") ref.current.resume();
    return ref.current;
  };
};

const PADS = [
  { id: "kick", label: "KICK", color: "#FFB703", freq: 120, type: "sine", drop: true, dropTo: 45 },
  { id: "snare", label: "SNARE", color: "#FF0055", noise: true, dur: 0.18 },
  { id: "hihat", label: "HI-HAT", color: "#00F0FF", noise: true, dur: 0.06 },
  { id: "racktom", label: "RACK TOM", color: "#FB8500", freq: 180, type: "sine", drop: true, dropTo: 95 },
  { id: "floortom", label: "FLOOR TOM", color: "#B565FF", freq: 110, type: "sine", drop: true, dropTo: 55 },
];

export const Soundboard = () => {
  const getCtx = useAudioCtx();

  const hit = (pad) => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    if (pad.noise) {
      const len = ctx.sampleRate * pad.dur;
      const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = pad.id === "hihat" ? "highpass" : "bandpass";
      filter.frequency.value = pad.id === "hihat" ? 6000 : 1800;
      src.connect(filter).connect(gain);
      gain.gain.setValueAtTime(0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + pad.dur);
      src.start(t);
    } else {
      const osc = ctx.createOscillator();
      osc.type = pad.type;
      osc.frequency.setValueAtTime(pad.freq, t);
      if (pad.drop) osc.frequency.exponentialRampToValueAtTime(pad.dropTo || 45, t + 0.15);
      osc.connect(gain);
      gain.gain.setValueAtTime(0.6, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + (pad.drop ? 0.22 : 0.5));
      osc.start(t);
      osc.stop(t + 0.55);
    }
  };

  return (
    <div data-testid="soundboard" className="glass-panel rounded-2xl p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <Drum className="h-4 w-4 text-gold" />
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
          Jam Along with the band while you wait
        </p>
      </div>
      <div className="grid grid-cols-5 gap-2.5">
        {PADS.map((pad) => (
          <button
            key={pad.id}
            data-testid={`pad-${pad.id}`}
            onClick={() => hit(pad)}
            className="aspect-square rounded-xl border border-white/15 bg-obsidian/70 font-mono text-[10px] sm:text-xs tracking-widest text-slate-300 hover:border-white/40 active:scale-90 transition-transform cursor-pointer"
            style={{ boxShadow: `inset 0 0 18px ${pad.color}22` }}
          >
            <span className="block h-2 w-2 rounded-full mx-auto mb-2" style={{ background: pad.color }} />
            {pad.label}
          </button>
        ))}
      </div>
    </div>
  );
};
